import { type UseQueryOptions, useQuery } from "@tanstack/react-query";

import type { EmployeeSearchServiceQueryParams } from "@performyard/service-contracts";

import { employeeSearchServiceQuery } from "../../../api/queries/defs/employee-search-service";

import { type EmployeeSearchProps, type EmployeesResponseData, fetchEmployees } from "../../services";

/**
 * Overview page hooks
 */
export const useEmployeeSearch = (props: EmployeeSearchProps, options?: UseQueryOptions<EmployeesResponseData>) => {
	return useQuery<EmployeesResponseData>({
		queryKey: ["employeesWithSelectionObject", props],
		queryFn: () => fetchEmployees(props),
		meta: {
			errorMessage: "There was an error getting the employees.",
		},
		...options,
	});
};

// Helper function to map employee data in the useEmployeePickerSearch hook
const mapEmployeeData = (employees) =>
	employees?.map((employee) => ({
		value: employee._id,
		label: employee.name,
	}));

// legacy data mapping handles different cases of data being returned
const mapLegacyEmployeeData = (legacyData) => {
	if (legacyData?.users) {
		return mapEmployeeData(legacyData?.users);
	}
	return legacyData;
};

/**
 * Custom hook to fetch employee data for the employee picker component.
 *
 * This hook can use either a legacy fetch function or the internal-api search service.
 * For all new development, the search service should be used.
 * The `version` parameter allows switching between the legacy and search service versions.
 * The `searchServiceProps` parameter allows passing additional parameters to the new search service.
 *
 * @param {string} queryKey - Unique key for the query.
 * @param {EmployeeSearchProps} props - Properties for searching employees.
 * @param {(() => any) | null} [overrideFetch=null] - Optional custom fetch function.
 * @param {"searchService" | "legacy"} [version="legacy"] - Version of the fetch function to use.
 * @param {Partial<EmployeeSearchServiceQueryParams>} [searchServiceProps] - Additional parameters for the search service.
 *
 * @returns {{ employeeData: any, isLoading: boolean }} - Employee data and loading state.
 */
export const useEmployeePickerSearch = (
	queryKey: string,
	{ searchTerm, excludeCurrentUser, exclusionList, filters, limitToDirectReports }: EmployeeSearchProps,
	overrideFetch: (() => any) | null = null,
	version: "searchService" | "legacy" = "legacy",
	searchServiceProps?: Partial<EmployeeSearchServiceQueryParams>
) => {
	const { data: legacyData, isLoading: legacyLoading } = useQuery({
		queryKey: [
			queryKey,
			searchTerm,
			overrideFetch,
			excludeCurrentUser,
			exclusionList,
			filters,
			limitToDirectReports,
		],
		queryFn: () =>
			overrideFetch
				? overrideFetch()
				: fetchEmployees({ searchTerm, excludeCurrentUser, exclusionList, filters, limitToDirectReports }),
		meta: {
			errorMessage: "There was an error getting the employees.",
		},
		enabled: version === "legacy",
	});

	const { data: searchServiceData, isLoading: searchServiceLoading } = useEmployeeSearchService({
		params: {
			...searchServiceProps,
			name_re: searchTerm,
		},
		enabled: version === "searchService",
	});
	const employeeData =
		version === "searchService" ? mapEmployeeData(searchServiceData?.results) : mapLegacyEmployeeData(legacyData);
	return {
		employeeData,
		isLoading: version === "searchService" ? searchServiceLoading : legacyLoading,
	};
};

export const useEmployeeSearchService = ({
	params,
	enabled,
}: {
	params: Partial<EmployeeSearchServiceQueryParams>;
	enabled: boolean;
}) => {
	return useQuery({ ...employeeSearchServiceQuery(params), enabled });
};
